import React, { createContext, FC, useMemo } from 'react';
import { OpenAPI } from '@rbx/docsite-utilities/dist/browser';

export const OpenAPIDocumentContext = createContext<OpenAPI.Document | null>(null);

export interface OpenAPIDocumentProviderProps {
  document: OpenAPI.Document;
}

/**
 * Recursively replaces all \r\n with <br> tags in string values throughout the document.
 * This ensures the markdown conversion reflects the behavior of old API Site documentation.
 */
const replaceCarriageReturnsWithBrTags = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return value.replace(/\r\n/g, '<br>');
  }

  if (Array.isArray(value)) {
    return value.map(replaceCarriageReturnsWithBrTags);
  }

  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    Object.entries(value).forEach(([key, val]) => {
      result[key] = replaceCarriageReturnsWithBrTags(val);
    });
    return result;
  }

  return value;
};

export const OpenAPIDocumentProvider: FC<React.PropsWithChildren<OpenAPIDocumentProviderProps>> = ({
  children,
  document,
}) => {
  const normalizedDocument = useMemo(
    () => replaceCarriageReturnsWithBrTags(document) as OpenAPI.Document,
    [document],
  );

  return (
    <OpenAPIDocumentContext.Provider value={normalizedDocument}>
      {children}
    </OpenAPIDocumentContext.Provider>
  );
};
